import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  padding: 4rem;
  position: relative;
  overflow: hidden;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 1rem;
  text-align: center;
  max-width: 1000px;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: #607C90;
  margin-bottom: 4rem;
  text-align: center;
  max-width: 900px;
  line-height: 1.6;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  max-width: 1400px;
  width: 100%;
  margin-bottom: 3rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: linear-gradient(90deg, #EF443C, #C43931);
  }
`;

const IconCircle = styled.div`
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, #EF443C, #C43931);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  box-shadow: 0 8px 20px rgba(239, 68, 60, 0.3);
`;

const FeatureName = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #042E4B;
  text-align: center;
`;

const FeatureDescription = styled.div`
  font-size: 1.2rem;
  color: #607C90;
  text-align: center;
  line-height: 1.6;
`;

const AIBadge = styled(motion.div)`
  background: linear-gradient(135deg, #042E4B, #0A4A6F);
  color: white;
  padding: 1.5rem 4rem;
  border-radius: 50px;
  font-size: 1.6rem;
  font-weight: 700;
  box-shadow: 0 8px 30px rgba(4, 46, 75, 0.4);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Slide8IA: React.FC = () => {
  const features = [
    {
      icon: '💡',
      name: 'Clareza',
      description: 'Insights automáticos que iluminam cada etapa, revelando tendências e oportunidades',
    },
    {
      icon: '🚀',
      name: 'Agilidade',
      description: 'Processos otimizados para um planejamento mais rápido e execução eficiente',
    },
    {
      icon: '🎯',
      name: 'Decisões Assertivas',
      description: 'Informações precisas para escolhas confiantes sem depender de consultores',
    },
  ];

  return (
    <Container>
      <Title
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        Inteligência Artificial a favor da estratégia
      </Title>

      <Subtitle
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        A ThinkWay integra recursos de IA que geram insights automáticos em cada etapa do planejamento
      </Subtitle>

      <CardsGrid>
        {features.map((feature, index) => (
          <FeatureCard
            key={`ia-${feature.name}`}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.4,
              delay: 0.2 + index * 0.1,
              type: 'spring',
              stiffness: 80,
            }}
          >
            <IconCircle>{feature.icon}</IconCircle>
            <FeatureName>{feature.name}</FeatureName>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </CardsGrid>

      <AIBadge
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.7 }}
      >
        <span>🧠</span>
        <span>Powered by AI</span>
      </AIBadge>
    </Container>
  );
};

export default Slide8IA;
